import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormII_Salim_fromRow } from './utils/arPresentFormII_salim';
import { conjugatePresentFormIII_Salim_fromRow } from './utils/arPresentFormIII_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeFromRowII } from './utils/imperativeFormII_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import { buildParticiplesFromRoot } from './utils/participlesFormI_salim';
import { buildParticiplesFromRowNonFormI } from './utils/participlesNonFormI';
import { buildGeneratedMasdar } from './utils/masdarPatterns';
import {
  arrangeEntriesByBinyanRules,
  buildPrimaryHeavyBinyanRules,
  getLowerBinyanNumbers
} from './utils/analysisBinyanMixing';
import html2canvas from 'html2canvas';
import './VerbAnalysisGame.css';

const SOURCE_FORM_LABEL = 'המקור';
const SOURCE_PRONOUN_VALUE = '__source_no_pronoun__';
const SOURCE_PRONOUN_MESSAGE = 'אין לצורת המקור גוף';

const IMPERATIVE_PRONOUN_MAP = {
  'you_ms': 'أَنْتَ',
  'you_fs': 'أَنْتِ',
  'you_mp': 'أَنْتُمْ',
  'you_fp': 'أَنْتُنَّ'
};

const GENDER_OPTIONS = [
  { id: 'masculine', nameHeb: 'יחיד', nameAr: 'مفرد مذكر' },
  { id: 'feminine', nameHeb: 'יחידה', nameAr: 'مفرد مؤنث' },
  { id: 'plural', nameHeb: 'רבים', nameAr: 'جمع مذكر' },
  { id: 'pluralFeminine', nameHeb: 'רבות', nameAr: 'جمع مؤنث' }
];

const isAllowed = (allowedValue, genderId) => {
  if (!allowedValue || allowedValue.trim() === '') return false;
  
  const genderMap = {
    'masculine': 'sm',
    'feminine': 'sf',
    'plural': 'pm',
    'pluralFeminine': 'pf'
  };
  
  const allowedStr = allowedValue.toLowerCase().trim();
  if (allowedStr === 'all') return true;
  
  const genderCode = genderMap[genderId];
  if (!genderCode) return false;
  
  const allowedCodes = allowedStr.split(',').map(s => s.trim());
  return allowedCodes.includes(genderCode);
};

const stripArabicDiacritics = (value) =>
  String(value || '')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, '')
    .trim();

const normalizeSurfaceForm = (value) =>
  String(value || '')
    .normalize('NFC')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, '')
    .trim();

const getMasdarPatternId = (value) => {
  const clean = stripArabicDiacritics(value);
  if (!clean) return null;
  if (clean.startsWith('م') && clean.endsWith('ة')) return 'mufa3ala';
  if (clean.length === 4) return 'fi3al';
  return null;
};

const getMasdarOptionsFromRow = (row) => {
  const seen = new Set();

  return [row?.makor1, row?.makor2]
    .map((value) => String(value || '').replace(/\u00A0/g, ' ').trim())
    .filter((value) => {
      const key = normalizeSurfaceForm(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getMasdarFromCsv = (row) => {
  return getMasdarOptionsFromRow(row)[0] || null;
};

const shuffleArray = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const pickRandom = (items) => items[Math.floor(Math.random() * items.length)];

const PRIMARY_OPENING_ATTEMPTS = 80;
const COMPLETE_SELECTION_ATTEMPTS = 80;
const BINYAN_ONE_PRESENT_CHANCE = 0.6;
const COMPLETE_GAME_PARTICIPLE_OPTIONS = [
  ['בינוני פועל', 'בינוני פועל', 'בינוני פעול'],
  ['בינוני פועל', 'בינוני פעול', 'בינוני פעול']
];
const COMPLETE_GAME_MIXED_SOURCES = {
  '2': ['1', '1'],
  '3': ['1', '2']
};
const getMixedSourcesForPrimary = (primaryBinyan, desiredCount = 2) => {
  if (primaryBinyan === '4') {
    return ['1', pickRandom(['2', '3'])];
  }

  const configuredSources = COMPLETE_GAME_MIXED_SOURCES[primaryBinyan];
  if (configuredSources) {
    return [...configuredSources];
  }

  return shuffleArray(getLowerBinyanNumbers(primaryBinyan)).slice(0, desiredCount);
};

const arrangePrimaryOpeningQuestions = (questions, primaryBinyan) => {
  const primaryQuestions = shuffleArray(
    questions.filter(question => question.sourceBinyan === primaryBinyan)
  );
  const minorityQuestions = shuffleArray(
    questions.filter(question => question.sourceBinyan !== primaryBinyan)
  );
  const guaranteedFirstQuestions = primaryQuestions.splice(0, Math.min(2, primaryQuestions.length));
  const remainingQuestions = [...primaryQuestions, ...minorityQuestions];

  for (let attempt = 0; attempt < PRIMARY_OPENING_ATTEMPTS; attempt += 1) {
    const shuffledRemainingQuestions = shuffleArray(remainingQuestions);
    const hasAdjacentMinorityQuestions = shuffledRemainingQuestions.some((question, index) => (
      index < shuffledRemainingQuestions.length - 1 &&
      question.sourceBinyan !== primaryBinyan &&
      shuffledRemainingQuestions[index + 1].sourceBinyan !== primaryBinyan
    ));

    if (!hasAdjacentMinorityQuestions || minorityQuestions.length <= 1) {
      return [...guaranteedFirstQuestions, ...shuffledRemainingQuestions];
    }
  }

  return [...guaranteedFirstQuestions, ...shuffleArray(remainingQuestions)];
};

const getPassiveFeminineMasdarMatch = (row, participlesBuilder) => {
  const participles = typeof participlesBuilder === 'function' ? participlesBuilder(row) : null;
  const passiveFeminine = participles?.passive?.feminine || null;
  if (!passiveFeminine) return null;

  if (!isAllowed(row?.benoni_paul_allowed || '', 'feminine')) {
    return null;
  }

  const matchingMasdar = getMasdarOptionsFromRow(row).find(
    (masdar) =>
      getMasdarPatternId(masdar) === 'mufa3ala' &&
      normalizeSurfaceForm(masdar) === normalizeSurfaceForm(passiveFeminine)
  );

  if (!matchingMasdar) {
    return null;
  }

  return {
    passiveFeminine,
    masdar: matchingMasdar
  };
};

const getQuestionMasdarEntries = (row, sourceConfig, question) => {
  const sourceBinyan = String(question?.sourceBinyan || '').replace('בניין ', '').trim();

  if (sourceBinyan !== '3') {
    const masdar = typeof sourceConfig?.buildMasdar === 'function' ? sourceConfig.buildMasdar(row) : null;
    return masdar ? [{ value: masdar, patternId: getMasdarPatternId(masdar) }] : [];
  }

  const ambiguousMatch = getPassiveFeminineMasdarMatch(row, sourceConfig?.participlesBuilder);

  return getMasdarOptionsFromRow(row)
    .map((value) => ({
      value,
      patternId: getMasdarPatternId(value)
    }))
    .filter(({ value, patternId }) => {
      if (question?.masdarPattern === 'fi3al') {
        return patternId === 'fi3al';
      }

      if (patternId === 'fi3al') return true;
      if (patternId !== 'mufa3ala') return false;

      return Boolean(
        ambiguousMatch &&
        normalizeSurfaceForm(ambiguousMatch.masdar) === normalizeSurfaceForm(value)
      );
    });
};

const getEligibleParticipleGenders = (row, targetForm, sourceConfig, question) => {
  const allowedField = targetForm === 'בינוני פועל' ? 'benoni_poel_allowed' : 'benoni_paul_allowed';
  const allowedValue = row?.[allowedField] || '';
  const sourceBinyan = String(question?.sourceBinyan || '').replace('בניין ', '').trim();
  const ambiguousMatch = targetForm === 'בינוני פעול' && sourceBinyan === '3'
    ? getPassiveFeminineMasdarMatch(row, sourceConfig?.participlesBuilder)
    : null;

  return GENDER_OPTIONS
    .map((gender) => gender.id)
    .filter((genderId) => {
      if (!isAllowed(allowedValue, genderId)) return false;
      if (targetForm === 'בינוני פעול' && sourceBinyan === '3' && genderId === 'feminine') {
        return Boolean(ambiguousMatch);
      }
      return true;
    });
};

const getQuestionAnswerVariantsResolved = ({
  question,
  targetTense,
  displayedVerb,
  row,
  sourceConfig,
  targetGender,
  primaryPronoun
}) => {
  const variants = [{
    form: targetTense,
    pronoun: primaryPronoun ?? null
  }];
  const sourceBinyan = String(question?.sourceBinyan || '').replace('בניין ', '').trim();

  if (sourceBinyan !== '3') {
    return variants;
  }

  const ambiguousMatch = getPassiveFeminineMasdarMatch(row, sourceConfig?.participlesBuilder);
  if (!ambiguousMatch) {
    return variants;
  }

  const normalizedDisplayedVerb = normalizeSurfaceForm(displayedVerb);

  if (
    targetTense === SOURCE_FORM_LABEL &&
    getMasdarPatternId(displayedVerb) === 'mufa3ala' &&
    normalizeSurfaceForm(ambiguousMatch.masdar) === normalizedDisplayedVerb
  ) {
    variants.push({
      form: 'בינוני פעול',
      pronoun: 'feminine'
    });
  }

  if (
    targetTense === 'בינוני פעול' &&
    targetGender === 'feminine' &&
    normalizeSurfaceForm(ambiguousMatch.passiveFeminine) === normalizedDisplayedVerb
  ) {
    variants.push({
      form: SOURCE_FORM_LABEL,
      pronoun: SOURCE_PRONOUN_VALUE
    });
  }

  return variants;
};

const DEFAULT_GAME_CONFIG = {
  title: 'ניתוח פעלים מלא - כולל המקור',
  shareTitle: 'תוצאות ניתוח פעלים מלא',
  shareTextLabel: 'ניתוח פעלים מלא - כולל המקור',
  shareFileName: 'תוצאות-ניתוח-פעלים-מלא-בניין-1.png',
  csvPath: '/verbs-grade10.csv',
  binyan: '1',
  allowedGizraPatterns: ['שלמים'],
  presentConjugator: conjugatePresentFormI_Salim_fromRow,
  imperativeBuilder: buildImperativeFromRow,
  participlesBuilder: buildParticiplesFromRoot,
  requiresFutureVowelForImperative: true,
  buildMasdar: getMasdarFromCsv
};

const BINYAN_GAME_CONFIGS = {
  '1': {
    allowedGizraPatterns: ['שלמים'],
    presentConjugator: conjugatePresentFormI_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRow,
    participlesBuilder: buildParticiplesFromRoot,
    requiresFutureVowelForImperative: true,
    buildMasdar: getMasdarFromCsv
  },
  '2': {
    title: 'ניתוח פעלים מלא - בניין 2 כולל המקור',
    shareTitle: 'תוצאות ניתוח פעלים מלא',
    shareTextLabel: 'ניתוח פעלים מלא - בניין 2 כולל המקור',
    shareFileName: 'תוצאות-ניתוח-פעלים-מלא-בניין-2.png',
    allowedGizraPatterns: ['שלמים', 'כפולים', 'ע"ו', 'פ"ו'],
    presentConjugator: conjugatePresentFormII_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRowII,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    requiresFutureVowelForImperative: false,
    buildMasdar: buildGeneratedMasdar
  },
  '3': {
    title: 'ניתוח פעלים מלא - בניין 3 כולל המקור',
    shareTitle: 'תוצאות ניתוח פעלים מלא',
    shareTextLabel: 'ניתוח פעלים מלא - בניין 3 כולל המקור',
    shareFileName: 'תוצאות-ניתוח-פעלים-מלא-בניין-3.png',
    allowedGizraPatterns: ['שלמים', 'ע"ו', 'פ"ו'],
    presentConjugator: conjugatePresentFormIII_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRowIII,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    requiresFutureVowelForImperative: false,
    buildMasdar: getMasdarFromCsv
  },
  '4': {
    title: 'ניתוח פעלים מלא - בניין 4 כולל המקור',
    shareTitle: 'תוצאות ניתוח פעלים מלא',
    shareTextLabel: 'ניתוח פעלים מלא - בניין 4 כולל המקור',
    shareFileName: 'תוצאות-ניתוח-פעלים-מלא-בניין-4.png',
    allowedGizraPatterns: ['שלמים'],
    presentConjugator: conjugatePresentFormIV_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRowIV,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    requiresFutureVowelForImperative: false,
    buildMasdar: buildGeneratedMasdar
  }
};

function VerbAnalysisGameComplete({ gameConfig = DEFAULT_GAME_CONFIG }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  const binyanDefaults = BINYAN_GAME_CONFIGS[gameConfig?.binyan || DEFAULT_GAME_CONFIG.binyan] || BINYAN_GAME_CONFIGS[DEFAULT_GAME_CONFIG.binyan];
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const config = {
    ...DEFAULT_GAME_CONFIG,
    ...binyanDefaults,
    ...gameConfig
  };
  const {
    binyan: targetBinyan,
    csvPath,
    shareFileName,
    shareTextLabel,
    shareTitle,
    title
  } = config;
  const primaryBinyan = String(targetBinyan || DEFAULT_GAME_CONFIG.binyan).replace('בניין ', '').trim();
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [questionPlan, setQuestionPlan] = useState([]);
  const [genderOrder, setGenderOrder] = useState([]);
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswers, setUserAnswers] = useState({
    root: null,
    binyan: null,
    form: null,
    pronoun: null
  });
  const [feedback, setFeedback] = useState({
    root: null,
    binyan: null,
    form: null,
    pronoun: null
  });
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [showFixPrompt, setShowFixPrompt] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [pulseFields, setPulseFields] = useState({});
  const [isUserSuccess, setIsUserSuccess] = useState(false);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  const audioContextRef = React.useRef(null);
  
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  React.useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const pronouns = [
    { arabic: 'أَنَا', hebrew: 'أَنَا - מדבר/ת' },
    { arabic: 'أَنْتَ', hebrew: 'أَنْتَ - נוכח' },
    { arabic: 'أَنْتِ', hebrew: 'أَنْتِ - נוכחת' },
    { arabic: 'هُوَ', hebrew: 'هُوَ - נסתר' },
    { arabic: 'هِيَ', hebrew: 'هِيَ - נסתרת' },
    { arabic: 'نَحْنُ', hebrew: 'نَحْنُ - מדברים/ות' },
    { arabic: 'أَنْتُمْ', hebrew: 'أَنْتُمْ - נוכחים' },
    { arabic: 'أَنْتُنَّ', hebrew: 'أَنْتُنَّ - נוכחות' },
    { arabic: 'هُمْ', hebrew: 'هُمْ - נסתרים' },
    { arabic: 'هُنَّ', hebrew: 'هُنَّ - נסתרות' }
  ];

  const forms = [
    { id: 'עבר', name: 'עבר' },
    { id: 'הווה-עתיד', name: 'הווה-עתיד' },
    { id: 'ציווי', name: 'ציווי' },
    { id: 'בינוני פועל', name: 'בינוני פועל' },
    { id: 'בינוני פעול', name: 'בינוני פעול' },
    { id: SOURCE_FORM_LABEL, name: SOURCE_FORM_LABEL }
  ];

  const binyans = ['1 فَعَلَ', '2 فَعَّلَ', '3 فَاعَلَ', '4 أَفْعَلَ', '5 تَفَعَّلَ', '6 تَفَاعَلَ', '7 إِنْفَعَلَ', '8 إِفْتَعَلَ', '10 إِسْتَفْعَلَ '];

  const createBalancedQuestionPlan = () => {
    const fixedQuestions = [
      { form: 'עבר', sourceBinyan: primaryBinyan },
      { form: 'עבר', sourceBinyan: primaryBinyan },
      { form: 'הווה-עתיד', sourceBinyan: primaryBinyan },
      { form: 'הווה-עתיד', sourceBinyan: primaryBinyan },
      { form: 'ציווי', sourceBinyan: primaryBinyan },
      primaryBinyan === '3'
        ? { form: SOURCE_FORM_LABEL, sourceBinyan: primaryBinyan, masdarPattern: 'fi3al' }
        : { form: SOURCE_FORM_LABEL, sourceBinyan: primaryBinyan },
      { form: SOURCE_FORM_LABEL, sourceBinyan: primaryBinyan }
    ];
    const selectedParticiples = pickRandom(COMPLETE_GAME_PARTICIPLE_OPTIONS)
      .map((form) => ({ form, sourceBinyan: primaryBinyan }));
    const basePlan = [...fixedQuestions, ...selectedParticiples];
    const mixedSources = getMixedSourcesForPrimary(primaryBinyan);

    if (!mixedSources) {
      return shuffleArray(basePlan);
    }

    const plan = basePlan.map((question) => ({ ...question }));
    const formCounts = plan.reduce((counts, question) => {
      counts[question.form] = (counts[question.form] || 0) + 1;
      return counts;
    }, {});
    if (primaryBinyan === '4') {
      const replaceableForms = Object.entries(formCounts)
        .filter(([, count]) => count >= 2)
        .map(([form]) => form);
      const binyanOneForm = Math.random() < BINYAN_ONE_PRESENT_CHANCE
        ? 'הווה-עתיד'
        : pickRandom(replaceableForms.filter(form => form !== 'הווה-עתיד'));
      const [secondaryBinyan] = mixedSources.filter(source => source !== '1');
      const secondaryForm = pickRandom(replaceableForms.filter(form => form !== binyanOneForm));
      const assignSourceToForm = (form, sourceBinyan) => {
        const indexes = plan
          .map((question, index) => (
            question.form === form && question.sourceBinyan === primaryBinyan ? index : -1
          ))
          .filter(index => index !== -1);
        const targetIndex = pickRandom(indexes);

        plan[targetIndex] = {
          ...plan[targetIndex],
          sourceBinyan
        };
      };

      // Only replace duplicated forms so every grammatical form remains represented by binyan 4.
      assignSourceToForm(binyanOneForm, '1');
      assignSourceToForm(secondaryForm, secondaryBinyan);

      return arrangeEntriesByBinyanRules(
        plan,
        buildPrimaryHeavyBinyanRules(primaryBinyan)
      );
    }

    const replacementForms = shuffleArray(
      Object.entries(formCounts)
        .filter(([, count]) => count >= 2)
        .map(([form]) => form)
    ).slice(0, mixedSources.length);
    const shuffledMixedSources = shuffleArray([...mixedSources]);

    replacementForms.forEach((replacementForm, replacementIndex) => {
      const replaceableIndexes = plan
        .map((question, index) => {
          if (question.form !== replacementForm || question.sourceBinyan !== primaryBinyan) {
            return -1;
          }

          if (
            primaryBinyan === '3' &&
            replacementForm === SOURCE_FORM_LABEL &&
            question.masdarPattern === 'fi3al'
          ) {
            return -1;
          }

          return index;
        })
        .filter(index => index !== -1);
      const targetIndex = pickRandom(replaceableIndexes);

      if (targetIndex !== undefined) {
        plan[targetIndex] = {
          ...plan[targetIndex],
          sourceBinyan: shuffledMixedSources[replacementIndex]
        };
      }
    });

    if (primaryBinyan === '2') {
      return arrangePrimaryOpeningQuestions(plan, primaryBinyan);
    }

    return arrangeEntriesByBinyanRules(
      plan,
      buildPrimaryHeavyBinyanRules(primaryBinyan)
    );
  };

  const getQuestionConfigForBinyan = (sourceBinyan) => {
    const normalizedBinyan = String(sourceBinyan || primaryBinyan).replace('בניין ', '').trim();
    return BINYAN_GAME_CONFIGS[normalizedBinyan] || null;
  };

  const getEligibleRowsForQuestion = (rows, targetForm, sourceBinyan) => {
    if (typeof targetForm === 'object' && targetForm !== null) {
      const question = targetForm;
      const normalizedSourceBinyan = question.sourceBinyan ?? sourceBinyan;
      const resolvedTargetForm = question.form;
      const sourceConfig = getQuestionConfigForBinyan(normalizedSourceBinyan);
      if (!sourceConfig) return [];

      return rows.filter(row => {
        const rowBinyan = (row['בניין'] || row['׳‘׳ ׳™׳™׳'] || '').trim();
        const gizra = (row.gizra || '').trim();
        const tense = (row['זמן'] || row['׳–׳׳'] || '').trim();
        const hasFaVowelFuture = (row.R2_FUTURE || row.fa_vowel_future || '').trim();
        const matchesBinyan =
          rowBinyan === normalizedSourceBinyan ||
          rowBinyan === `בניין ${normalizedSourceBinyan}` ||
          rowBinyan === `׳‘׳ ׳™׳™׳ ${normalizedSourceBinyan}`;

        if (!matchesBinyan) return false;
        if (!sourceConfig.allowedGizraPatterns.some(pattern => gizra.includes(pattern))) return false;
        if (tense !== 'עבר' && tense !== '׳¢׳‘׳¨') return false;
        if (sourceConfig.requiresFutureVowelForImperative && !hasFaVowelFuture) return false;

        if (resolvedTargetForm === 'בינוני פעול' || resolvedTargetForm === '׳‘׳™׳ ׳•׳ ׳™ ׳₪׳¢׳•׳') {
          return getEligibleParticipleGenders(row, 'בינוני פעול', sourceConfig, question).length > 0;
        }

        if (resolvedTargetForm === 'בינוני פועל' || resolvedTargetForm === '׳‘׳™׳ ׳•׳ ׳™ ׳₪׳•׳¢׳') {
          return getEligibleParticipleGenders(row, 'בינוני פועל', sourceConfig, question).length > 0;
        }

        if (resolvedTargetForm === SOURCE_FORM_LABEL) {
          return getQuestionMasdarEntries(row, sourceConfig, question).length > 0;
        }

        return true;
      });
    }

    const sourceConfig = getQuestionConfigForBinyan(sourceBinyan);
    if (!sourceConfig) return [];

    return rows.filter(row => {
      const rowBinyan = (row['בניין'] || '').trim();
      const gizra = (row['gizra'] || '').trim();
      const tense = (row['זמן'] || '').trim();
      const hasFaVowelFuture = (row['R2_FUTURE'] || row['fa_vowel_future'] || '').trim();
      const matchesBinyan = rowBinyan === sourceBinyan || rowBinyan === `בניין ${sourceBinyan}`;

      if (!matchesBinyan) return false;
      if (!sourceConfig.allowedGizraPatterns.some(pattern => gizra.includes(pattern))) return false;
      if (tense !== 'עבר') return false;
      if (sourceConfig.requiresFutureVowelForImperative && !hasFaVowelFuture) return false;

      if (targetForm === 'בינוני פעול') {
        return (row['benoni_paul_allowed'] || '').trim() !== '';
      }

      if (targetForm === 'בינוני פועל') {
        return (row['benoni_poel_allowed'] || '').trim() !== '';
      }

      if (targetForm === SOURCE_FORM_LABEL) {
        const masdar = typeof sourceConfig.buildMasdar === 'function' ? sourceConfig.buildMasdar(row) : null;
        return Boolean(masdar);
      }

      return true;
    });
  };

  const buildQuestionSet = (rows, questionPlan, enforceUniqueParticipleGenders) => {
    const selectedVerbs = [];
    const genderSequence = [];
    const usedActiveGenders = new Set();
    const usedPassiveGenders = new Set();

    for (const question of questionPlan) {
      const { form: targetTense } = question;
      const sourceConfig = getQuestionConfigForBinyan(question.sourceBinyan);
      const availableVerbs = getEligibleRowsForQuestion(rows, question);
      const usedRoots = selectedVerbs.map(verb => verb['שורש']);
      const unusedVerbs = availableVerbs.filter(verb => !usedRoots.includes(verb['שורש']));
      const poolToUse = unusedVerbs.length > 0 ? unusedVerbs : availableVerbs;

      if (!sourceConfig || poolToUse.length === 0) {
        return null;
      }

      let selectedVerb = null;
      let selectedGender = null;

      if (targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול') {
        const usedGenders = targetTense === 'בינוני פועל' ? usedActiveGenders : usedPassiveGenders;
        const candidates = poolToUse
          .map((verb) => {
            const availableGenders = getEligibleParticipleGenders(verb, targetTense, sourceConfig, question);
            return {
              verb,
              availableGenders,
              unusedGenders: availableGenders.filter(gender => !usedGenders.has(gender))
            };
          })
          .filter(candidate => candidate.availableGenders.length > 0);

        if (candidates.length === 0) {
          return null;
        }

        const candidatesWithUnusedGenders = candidates.filter(candidate => candidate.unusedGenders.length > 0);
        if (enforceUniqueParticipleGenders && candidatesWithUnusedGenders.length === 0) {
          return null;
        }

        const candidatePool = enforceUniqueParticipleGenders
          ? candidatesWithUnusedGenders
          : (candidatesWithUnusedGenders.length > 0 ? candidatesWithUnusedGenders : candidates);
        const chosenCandidate = pickRandom(candidatePool);
        const genderPool = chosenCandidate.unusedGenders.length > 0
          ? chosenCandidate.unusedGenders
          : chosenCandidate.availableGenders;

        selectedVerb = chosenCandidate.verb;
        selectedGender = pickRandom(genderPool) || null;

        if (selectedGender) {
          usedGenders.add(selectedGender);
        }
      } else {
        selectedVerb = pickRandom(poolToUse);
      }

      if (!selectedVerb) {
        return null;
      }

      selectedVerbs.push(selectedVerb);
      genderSequence.push(selectedGender);
    }

    return {
      questionPlan,
      selectedVerbs,
      genderSequence
    };
  };

  useEffect(() => {
    const loadVerbs = async () => {
      try {
        setIsLoaded(false);
        setLoadError('');
        setShowResults(false);
        setCurrentQuestion(null);
        setCurrentVerbIndex(0);
        setGameVerbs([]);
        setQuestionPlan([]);
        setGenderOrder([]);

        const rows = await loadVerbsCsv(csvPath);
        const shouldPreferUniqueParticipleGenders = primaryBinyan === '2' || primaryBinyan === '3';
        let selection = null;

        for (let attempt = 0; attempt < COMPLETE_SELECTION_ATTEMPTS; attempt += 1) {
          const candidatePlan = createBalancedQuestionPlan();
          selection = buildQuestionSet(rows, candidatePlan, shouldPreferUniqueParticipleGenders);
          if (selection) {
            break;
          }
        }

        if (!selection && shouldPreferUniqueParticipleGenders) {
          for (let attempt = 0; attempt < COMPLETE_SELECTION_ATTEMPTS; attempt += 1) {
            const candidatePlan = createBalancedQuestionPlan();
            selection = buildQuestionSet(rows, candidatePlan, false);
            if (selection) {
              break;
            }
          }
        }

        if (!selection) {
          const activeRows = getEligibleRowsForQuestion(rows, { form: 'בינוני פועל', sourceBinyan: primaryBinyan });
          const passiveRows = getEligibleRowsForQuestion(rows, { form: 'בינוני פעול', sourceBinyan: primaryBinyan });

          if (activeRows.length === 0 || passiveRows.length === 0) {
            setLoadError(`לבניין ${primaryBinyan} עדיין חסרים בטבלה נתוני בינוני פועל/פעול, ולכן המשחק הזה עדיין לא זמין.`);
            return;
          }

          setLoadError('לא נמצאו מספיק פעלים מתאימים לכל סוגי השאלות במשחק הזה.');
          return;
        }

        setGameVerbs(selection.selectedVerbs);
        setQuestionPlan(selection.questionPlan);
        setGenderOrder(selection.genderSequence);
        generateQuestion(selection.selectedVerbs, 0, selection.questionPlan, selection.genderSequence);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
        setLoadError('שגיאה בטעינת הפעלים למשחק.');
      } finally {
        setIsLoaded(true);
      }
    };

    loadVerbs();
    // The selection helpers depend on the active game config and should rerun only when the route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvPath, primaryBinyan]);

  const removeNiqqud = (str) => str.replace(/[\u064B-\u065F]/g, '');
  
  const extractBinyanNumber = (binyanStr) => {
    if (!binyanStr) return null;
    const match = binyanStr.match(/^\d+/);
    return match ? match[0] : binyanStr;
  };
  
  const getBinyanDisplay = (binyanNumber) => {
    if (!binyanNumber) return '';
    const number = extractBinyanNumber(binyanNumber);
    const found = binyans.find(b => extractBinyanNumber(b) === number);
    return found || binyanNumber;
  };

  const extractLetters = (verb) => {
    const clean = removeNiqqud(verb);
    return clean.split('').filter(c => /[\u0621-\u064A]/.test(c));
  };

  const generateRootOptions = (correctRoot, verb, binyan, pronoun) => {
    const options = [correctRoot];
    const verbLetters = extractLetters(verb);
    
    if (verbLetters.length === 3) {
      const letters = verbLetters;
      const permutations = new Set();
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          for (let k = 0; k < 3; k++) {
            if (i !== j && j !== k && i !== k) {
              const perm = letters[i] + letters[j] + letters[k];
              if (perm !== correctRoot) {
                permutations.add(perm);
              }
            }
          }
        }
      }
      const permArray = Array.from(permutations).sort(() => Math.random() - 0.5);
      options.push(...permArray.slice(0, 3));
      if (options.length < 4) {
        const arabicLetters = 'بتثجحخدذرزسشصضطظعغفقكلمنهوي'.split('');
        let fillAttempts = 0;
        while (options.length < 4 && fillAttempts < 50) {
          fillAttempts++;
          const randomLetter = arabicLetters[Math.floor(Math.random() * arabicLetters.length)];
          const pos = Math.floor(Math.random() * 3);
          const newRoot = [...letters];
          newRoot[pos] = randomLetter;
          const candidate = newRoot.join('');
          if (candidate.length === 3 && candidate !== correctRoot && !options.includes(candidate)) {
            options.push(candidate);
          }
        }
      }
      return options.sort(() => Math.random() - 0.5);
    }
    
    {
      const wrongOptions = new Set();
      
      for (let i = 0; i <= verbLetters.length - 3; i++) {
        const consecutive = verbLetters.slice(i, i + 3).join('');
        if (consecutive !== correctRoot && !wrongOptions.has(consecutive)) {
          wrongOptions.add(consecutive);
        }
      }
      
      if (wrongOptions.size < 3) {
        if (verbLetters.length >= 4) {
          const skip1 = verbLetters[0] + verbLetters[2] + verbLetters[3];
          if (skip1 !== correctRoot && !wrongOptions.has(skip1)) {
            wrongOptions.add(skip1);
          }
        }
        
        if (verbLetters.length >= 5) {
          const skip2 = verbLetters[1] + verbLetters[3] + verbLetters[4];
          if (skip2 !== correctRoot && !wrongOptions.has(skip2)) {
            wrongOptions.add(skip2);
          }
        }
        
        if (verbLetters.length >= 5) {
          const skip3 = verbLetters[1] + verbLetters[2] + verbLetters[4];
          if (skip3 !== correctRoot && !wrongOptions.has(skip3)) {
            wrongOptions.add(skip3);
          }
        }
      }
      
      let attempts = 0;
      while (wrongOptions.size < 3 && attempts < 50) {
        attempts++;
        const shuffledLetters = [...verbLetters].sort(() => Math.random() - 0.5);
        const wrongRoot = shuffledLetters.slice(0, 3).join('');
        
        if (wrongRoot.length === 3 && wrongRoot !== correctRoot && !wrongOptions.has(wrongRoot)) {
          wrongOptions.add(wrongRoot);
        }
      }
      
      options.push(...Array.from(wrongOptions).slice(0, 3));
    }
    
    return options.sort(() => Math.random() - 0.5);
  };

  const generateQuestion = (verbs, index, questionPlanSequence, genderSequence) => {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }
    
    const verbData = verbs[index];
    const question = questionPlanSequence[index];
    const { form: targetTense, sourceBinyan } = question;
    const targetGender = genderSequence[index];
    const sourceConfig = getQuestionConfigForBinyan(sourceBinyan);
    
    let conjugatedVerb;
    let conjugations;
    let randomPronoun = null;
    let genderInfo = null;
    
    if (targetTense === 'עבר') {
      conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
      if (conjugations) {
        const availablePronouns = pronouns.filter(p => conjugations[p.arabic]);
        randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === 'הווה-עתיד') {
      conjugations = sourceConfig?.presentConjugator ? sourceConfig.presentConjugator(verbData) : null;
      if (conjugations) {
        const availablePronouns = pronouns.filter(p => conjugations[p.arabic]);
        randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === 'ציווי') {
      const imperativeConj = sourceConfig?.imperativeBuilder ? sourceConfig.imperativeBuilder(verbData) : null;
      if (imperativeConj) {
        const imperativeKeys = ['you_ms', 'you_fs', 'you_mp', 'you_fp'];
        const randomKey = imperativeKeys[Math.floor(Math.random() * imperativeKeys.length)];
        conjugatedVerb = imperativeConj[randomKey];
        const arabicPronoun = IMPERATIVE_PRONOUN_MAP[randomKey];
        randomPronoun = pronouns.find(p => removeNiqqud(p.arabic) === removeNiqqud(arabicPronoun));
      }
    } else if (targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול') {
      const participles = sourceConfig?.participlesBuilder ? sourceConfig.participlesBuilder(verbData) : null;
      if (participles) {
        const gender = targetGender || 'masculine';
        if (targetTense === 'בינוני פועל') {
          conjugatedVerb = participles.active[gender];
        } else {
          conjugatedVerb = participles.passive[gender];
        }
        genderInfo = GENDER_OPTIONS.find(g => g.id === gender);
      }
    } else if (targetTense === SOURCE_FORM_LABEL) {
      const masdarEntries = getQuestionMasdarEntries(verbData, sourceConfig, question);
      const selectedMasdar = pickRandom(masdarEntries);
      if (selectedMasdar) {
        conjugatedVerb = selectedMasdar.value;
      }
    }
    
    if (!conjugatedVerb) {
      console.error('Failed to generate verb for:', targetTense, verbData);
      return;
    }
    
    const rootOptions = generateRootOptions(
      removeNiqqud(verbData['שורש']), 
      conjugatedVerb,
      verbData['בניין'],
      randomPronoun ? randomPronoun.arabic : ''
    );
    
    const isBenoni = targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול';
    const isMasdar = targetTense === SOURCE_FORM_LABEL;
    const answerVariants = getQuestionAnswerVariantsResolved({
      question,
      targetTense,
      displayedVerb: conjugatedVerb,
      row: verbData,
      sourceConfig,
      targetGender: genderInfo ? genderInfo.id : null,
      primaryPronoun: isMasdar
        ? SOURCE_PRONOUN_VALUE
        : (isBenoni ? (genderInfo ? genderInfo.id : null) : (randomPronoun ? randomPronoun.arabic : null))
    });
    
    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      sourceBinyan,
      correctAnswers: {
        root: removeNiqqud(verbData['שורש']),
        binyan: verbData['בניין'],
        form: targetTense,
        pronoun: isMasdar ? SOURCE_PRONOUN_VALUE : (isBenoni ? (genderInfo ? genderInfo.id : null) : (randomPronoun ? randomPronoun.arabic : null))
      },
      options: {
        roots: rootOptions,
        binyans: binyans,
        forms: forms,
        pronouns: pronouns,
        genders: GENDER_OPTIONS
      },
      answerVariants,
      pronoun: randomPronoun,
      genderInfo: genderInfo,
      isBenoni: isBenoni,
      isMasdar: isMasdar
    });
    
    setUserAnswers({
      root: null,
      binyan: null,
      form: null,
      pronoun: null
    });
    
    setFeedback({
      root: null,
      binyan: null,
      form: null,
      pronoun: null
    });
    
    setShowCorrectAnswer(false);
    setShowFixPrompt(false);
    setAttempts([]);
    setPulseFields({});
    setIsUserSuccess(false);
    setTooltipVisible(false);
  };

  const arePronounsEquivalent = (userValue, correctValue, form) => {
    if (!correctValue) return userValue === correctValue;
    if (form === SOURCE_FORM_LABEL || form === 'בינוני פועל' || form === 'בינוני פעול') {
      return userValue === correctValue;
    }

    const userPronounClean = removeNiqqud(userValue || '');
    const correctPronounClean = removeNiqqud(correctValue || '');
    if (userPronounClean === correctPronounClean) {
      return true;
    }

    if (form === 'הווה-עתיד') {
      const isUserAnta = userPronounClean === 'انت' || userPronounClean === 'أنت';
      const isUserHiya = userPronounClean === 'هي';
      const isCorrectAnta = correctPronounClean === 'انت' || correctPronounClean === 'أنت';
      const isCorrectHiya = correctPronounClean === 'هي';

      if ((isUserAnta && isCorrectHiya) || (isUserHiya && isCorrectAnta)) {
        return true;
      }
    }

    return false;
  };

  const findMatchingAnswerVariant = (variants, form, pronoun) => (
    variants.find(
      (variant) => variant.form === form && arePronounsEquivalent(pronoun, variant.pronoun, variant.form)
    ) || null
  );

  const checkAnswers = () => {
    if (!currentQuestion) return;

    const answerVariants = currentQuestion.answerVariants?.length
      ? currentQuestion.answerVariants
      : [{
          form: currentQuestion.correctAnswers.form,
          pronoun: currentQuestion.correctAnswers.pronoun
        }];
    const currentAttempt = { ...userAnswers };
    const newFeedback = {};
    let allCorrect = true;
    const isFirstAttempt = attempts.length === 0;

    const isRootCorrect = userAnswers.root === currentQuestion.correctAnswers.root;
    newFeedback.root = isRootCorrect ? 'correct' : 'wrong';
    if (!isRootCorrect) allCorrect = false;

    const isBinyanCorrect = extractBinyanNumber(userAnswers.binyan) === extractBinyanNumber(currentQuestion.correctAnswers.binyan);
    newFeedback.binyan = isBinyanCorrect ? 'correct' : 'wrong';
    if (!isBinyanCorrect) allCorrect = false;

    const isFormCorrect = answerVariants.some((variant) => variant.form === userAnswers.form);
    newFeedback.form = isFormCorrect ? 'correct' : 'wrong';
    if (!isFormCorrect) allCorrect = false;

    const exactVariantMatch = findMatchingAnswerVariant(answerVariants, userAnswers.form, userAnswers.pronoun);
    const hasPronounMatchForSelectedForm = answerVariants.some((variant) => (
      variant.form === userAnswers.form &&
      arePronounsEquivalent(userAnswers.pronoun, variant.pronoun, variant.form)
    ));
    const hasPronounMatchAcrossVariants = answerVariants.some((variant) => (
      arePronounsEquivalent(userAnswers.pronoun, variant.pronoun, variant.form)
    ));
    const isPronounCorrect = isFormCorrect
      ? hasPronounMatchForSelectedForm
      : hasPronounMatchAcrossVariants;
    newFeedback.pronoun = isPronounCorrect ? 'correct' : 'wrong';
    if (!isPronounCorrect) allCorrect = false;
    if (!exactVariantMatch) allCorrect = false;

    setFeedback(newFeedback);
    setAttempts(prev => [...prev, currentAttempt]);

    if (allCorrect) {
      setIsUserSuccess(true);
      playSuccessSound();
      setTimeout(() => {
        saveToHistory(isFirstAttempt);
        nextQuestion();
      }, 2000);
    } else {
      playErrorSound();
      setShowFixPrompt(true);
    }

    return;
  };

  const saveToHistory = (isCorrect) => {
    const answerVariants = currentQuestion.answerVariants?.length
      ? currentQuestion.answerVariants
      : [{
          form: currentQuestion.correctAnswers.form,
          pronoun: currentQuestion.correctAnswers.pronoun
        }];
    const matchedVariant = findMatchingAnswerVariant(answerVariants, userAnswers.form, userAnswers.pronoun);
    const savedCorrectAnswers = matchedVariant
      ? {
          ...currentQuestion.correctAnswers,
          form: matchedVariant.form,
          pronoun: matchedVariant.pronoun
        }
      : currentQuestion.correctAnswers;

    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      pronoun: currentQuestion.pronoun,
      genderInfo: currentQuestion.genderInfo,
      isBenoni: currentQuestion.isBenoni,
      isMasdar: currentQuestion.isMasdar,
      correctAnswers: savedCorrectAnswers,
      userAttempts: [...attempts],
      finalAnswer: userAnswers,
      isCorrect
    }]);
  };

  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, nextIndex, questionPlan, genderOrder);
  };

  const handleFix = () => {
    setShowFixPrompt(false);
  };

  const handleContinue = () => {
    setShowFixPrompt(false);
    setShowCorrectAnswer(true);
    
    const wrongFields = {};
    const updatedAnswers = { ...userAnswers };
    
    if (feedback.root === 'wrong') {
      wrongFields.root = true;
      updatedAnswers.root = currentQuestion.correctAnswers.root;
    }
    if (feedback.binyan === 'wrong') {
      wrongFields.binyan = true;
      updatedAnswers.binyan = getBinyanDisplay(currentQuestion.correctAnswers.binyan);
    }
    if (feedback.form === 'wrong') {
      wrongFields.form = true;
      updatedAnswers.form = currentQuestion.correctAnswers.form;
    }
    if (feedback.pronoun === 'wrong') {
      wrongFields.pronoun = true;
      updatedAnswers.pronoun = currentQuestion.correctAnswers.pronoun;
    }
    
    setUserAnswers(updatedAnswers);
    setPulseFields(wrongFields);
    setFeedback({
      root: 'correct',
      binyan: 'correct',
      form: 'correct',
      pronoun: 'correct'
    });
    
    saveToHistory(false);
  };
  
  const handleNextAfterCorrectAnswer = () => {
    setPulseFields({});
    setShowCorrectAnswer(false);
    nextQuestion();
  };

  const handleFormChange = (newForm) => {
    const prevForm = userAnswers.form;
    const wasParticiple = prevForm === 'בינוני פועל' || prevForm === 'בינוני פעול';
    const isNowParticiple = newForm === 'בינוני פועל' || newForm === 'בינוני פעול';
    const wasSourceForm = prevForm === SOURCE_FORM_LABEL;
    const isNowMasdar = newForm === SOURCE_FORM_LABEL;

    if (isNowMasdar) {
      setUserAnswers(prev => ({ ...prev, form: newForm, pronoun: SOURCE_PRONOUN_VALUE }));
      setFeedback(prev => ({ ...prev, pronoun: null }));
    } else if (wasSourceForm || wasParticiple !== isNowParticiple) {
      setUserAnswers(prev => ({ ...prev, form: newForm, pronoun: null }));
      setFeedback(prev => ({ ...prev, pronoun: null }));
    } else {
      setUserAnswers(prev => ({ ...prev, form: newForm }));
    }
    setTooltipVisible(false);
  };

  const handlePronounMouseMove = (e) => {
    if (!userAnswers.form && !showCorrectAnswer && !showFixPrompt && feedback.pronoun !== 'correct') {
      const isMobile = window.matchMedia('(max-width: 768px)').matches || e.pointerType === 'touch';
      
      if (isMobile) {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({ 
          x: rect.left + rect.width / 2, 
          y: rect.top 
        });
      } else {
        setTooltipPosition({ x: e.clientX, y: e.clientY });
      }
      setTooltipVisible(true);
    }
  };

  const handlePronounMouseLeave = () => {
    setTooltipVisible(false);
  };

  const isSelectedFormParticiple = userAnswers.form === 'בינוני פועל' || userAnswers.form === 'בינוני פעול';
  const isSelectedFormMasdar = userAnswers.form === SOURCE_FORM_LABEL;
  const showPronounTooltip = tooltipVisible && false;

  const playSuccessSound = () => {
    initAudioContext();
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const playErrorSound = () => {
    initAudioContext();
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  const formatPronounOrGender = (value, { forResults = false } = {}) => {
    if (!value) return '-';
    if (value === SOURCE_PRONOUN_VALUE) {
      return SOURCE_PRONOUN_MESSAGE;
    }
    const genderOption = GENDER_OPTIONS.find(g => g.id === value);
    if (genderOption) return genderOption.nameHeb;
    const pronounOption = pronouns.find(p => removeNiqqud(p.arabic) === removeNiqqud(value));
    if (pronounOption) {
      const hebrewShort = pronounOption.hebrew.split(' - ')[1] || pronounOption.hebrew;
      return `${pronounOption.arabic} ${hebrewShort}`;
    }
    return value;
  };

  const renderMixedResultText = (text) => {
    const value = text == null ? '' : String(text);
    const arabicSegmentPattern = /([\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF](?:[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s־-]*[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF])?)/g;
    const arabicCharPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

    return value.split(arabicSegmentPattern).map((part, index) => {
      if (!part) return null;
      if (!arabicCharPattern.test(part)) return part;

      return (
        <span key={index} className="analysis-result-arabic-part">
          {part}
        </span>
      );
    });
  };

  const generateResultsImage = async () => {
    const resultsElement = document.querySelector('.words-summary');
    if (!resultsElement) return null;
    const canvas = await html2canvas(resultsElement, {
      backgroundColor: '#5f9ea0',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = shareFileName;
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `${shareTextLabel}\nהשגתי ${gameHistory.filter(h => h.isCorrect).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        console.log('Share canceled or failed', err);
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = imageData;
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = shareFileName;
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (!isLoaded) {
    return <div className="loading">טוען...</div>;
  }

  if (loadError) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={() => navigate(getBackUrl())} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>{title}</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary">
            <h3>{loadError}</h3>
          </div>
        </main>
      </div>
    );
  }

  if (showResults) {
    const correctCount = gameHistory.filter(h => h.isCorrect).length;
    
    return (
      <div className="App verb-analysis-results">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={() => navigate(getBackUrl())} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary">
            <h3>{title}</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => {
                const binyanDisplay = getBinyanDisplay(item.correctAnswers.binyan);
                const isSourceForm = item.correctAnswers.form === SOURCE_FORM_LABEL;
                const pronounDisplay = isSourceForm ? '' : formatPronounOrGender(item.correctAnswers.pronoun, { forResults: true });
                const correctAnswerText = isSourceForm
                  ? `${item.correctAnswers.root}, ${binyanDisplay}, ${item.correctAnswers.form}`
                  : `${item.correctAnswers.root}, ${binyanDisplay}, ${item.correctAnswers.form}, ${pronounDisplay}`;
                
                let userAnswerText = '';
                if (!item.isCorrect && item.userAttempts.length > 0) {
                  const firstAttempt = item.userAttempts[0];
                  const userParts = [
                    firstAttempt.root || '-',
                    firstAttempt.binyan || '-',
                    firstAttempt.form || '-'
                  ];
                  if (!isSourceForm) {
                    userParts.push(formatPronounOrGender(firstAttempt.pronoun, { forResults: true }));
                  }
                  userAnswerText = userParts.join(', ');
                }
                
                return (
                  <div key={idx} className="word-item">
                    <div className="word-pair">
                      <span className="arabic-word-summary">{item.verb}</span>
                      <span className="separator"> - </span>
                      <span className="hebrew-word">
                        {renderMixedResultText(correctAnswerText)}
                      </span>
                    </div>
                    <div className={`attempts-info ${!item.isCorrect ? 'multiple-attempts' : ''}`}>
                      {item.isCorrect ? (
                        <span>✓ תשובה נכונה</span>
                      ) : (
                        <span>✗ תשובתך: {renderMixedResultText(userAnswerText)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק שוב
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="loading">טוען...</div>;
  }

  const allAnswered = userAnswers.root && userAnswers.binyan && userAnswers.form && userAnswers.pronoun;

  return (
    <div className="verb-analysis-game">
      <div className="game-header">
        <button onClick={() => navigate(getBackUrl())} className="back-to-verbs-button">
         ↩ חזרה למשחקי הפועל
        </button>
        <h2>{title}</h2>
        <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
      </div>

      <div className="verb-display">
        <div className="verb-arabic">{currentQuestion.verb}</div>
      </div>

      {isUserSuccess && (
        <div className="stars-animation">
          <div className="star">⭐</div>
          <div className="star">⭐</div>
          <div className="star">⭐</div>
        </div>
      )}

      <div className="analysis-grid">
        <div className={`analysis-section ${pulseFields.root ? 'pulse-container' : ''}`}>
          <label htmlFor="root-select">שורש</label>
          <select
            id="root-select"
            className={`analysis-dropdown ${
              feedback.root === 'wrong' ? 'wrong' : ''
            } ${feedback.root === 'correct' ? 'correct' : ''}`}
            value={userAnswers.root || ''}
            onChange={(e) => {
              if (!showCorrectAnswer && !showFixPrompt && feedback.root !== 'correct') {
                setUserAnswers(prev => ({ ...prev, root: e.target.value }));
                if (feedback.root === 'wrong') setFeedback(prev => ({ ...prev, root: null }));
              }
            }}
            disabled={showCorrectAnswer || showFixPrompt || feedback.root === 'correct'}
          >
            <option value="">בחר שורש...</option>
            {currentQuestion.options.roots.map((root, idx) => (
              <option key={idx} value={root}>{root}</option>
            ))}
          </select>
        </div>

        <div className={`analysis-section ${pulseFields.binyan ? 'pulse-container' : ''}`}>
          <label htmlFor="binyan-select">בניין</label>
          <select
            id="binyan-select"
            className={`analysis-dropdown ${
              feedback.binyan === 'wrong' ? 'wrong' : ''
            } ${feedback.binyan === 'correct' ? 'correct' : ''}`}
            value={userAnswers.binyan || ''}
            onChange={(e) => {
              if (!showCorrectAnswer && !showFixPrompt && feedback.binyan !== 'correct') {
                setUserAnswers(prev => ({ ...prev, binyan: e.target.value }));
                if (feedback.binyan === 'wrong') setFeedback(prev => ({ ...prev, binyan: null }));
              }
            }}
            disabled={showCorrectAnswer || showFixPrompt || feedback.binyan === 'correct'}
          >
            <option value="">בחר בניין...</option>
            {currentQuestion.options.binyans.map((binyan) => (
              <option key={binyan} value={binyan}>{binyan}</option>
            ))}
          </select>
        </div>

        <div className={`analysis-section ${pulseFields.form ? 'pulse-container' : ''}`}>
          <label htmlFor="form-select">צורה דקדוקית</label>
          <select
            id="form-select"
            className={`analysis-dropdown ${
              feedback.form === 'wrong' ? 'wrong' : ''
            } ${feedback.form === 'correct' ? 'correct' : ''}`}
            value={userAnswers.form || ''}
            onChange={(e) => {
              if (!showCorrectAnswer && !showFixPrompt && feedback.form !== 'correct') {
                handleFormChange(e.target.value);
                if (feedback.form === 'wrong') setFeedback(prev => ({ ...prev, form: null }));
              }
            }}
            disabled={showCorrectAnswer || showFixPrompt || feedback.form === 'correct'}
          >
            <option value="">בחר צורה...</option>
            {currentQuestion.options.forms.map((form) => (
              <option key={form.id} value={form.id}>{form.name}</option>
            ))}
          </select>
        </div>

        <div className={`analysis-section ${pulseFields.pronoun ? 'pulse-container' : ''}`}>
          <label htmlFor="pronoun-select">{isSelectedFormParticiple ? 'מין ומספר' : 'גוף'}</label>
          <div 
            className="dropdown-wrapper"
            onMouseMove={handlePronounMouseMove}
            onMouseLeave={handlePronounMouseLeave}
          >
            <select
              id="pronoun-select"
              className={`analysis-dropdown ${
                feedback.pronoun === 'wrong' ? 'wrong' : ''
              } ${feedback.pronoun === 'correct' ? 'correct' : ''}${isSelectedFormMasdar ? ' source-form-locked' : ''}`}
              value={userAnswers.pronoun || ''}
              onChange={(e) => {
                if (!showCorrectAnswer && !showFixPrompt && feedback.pronoun !== 'correct') {
                  setUserAnswers(prev => ({ ...prev, pronoun: e.target.value }));
                  if (feedback.pronoun === 'wrong') setFeedback(prev => ({ ...prev, pronoun: null }));
                }
              }}
              disabled={showCorrectAnswer || showFixPrompt || feedback.pronoun === 'correct' || isSelectedFormMasdar}
            >
              {isSelectedFormMasdar ? (
                <option value={SOURCE_PRONOUN_VALUE}>{SOURCE_PRONOUN_MESSAGE}</option>
              ) : (
                <>
                  <option value="">{isSelectedFormParticiple ? 'בחר מין ומספר...' : 'בחר גוף...'}</option>
                  {isSelectedFormParticiple ? (
                    currentQuestion.options.genders.map((gender) => (
                      <option key={gender.id} value={gender.id}>{gender.nameHeb}</option>
                    ))
                  ) : (
                    currentQuestion.options.pronouns.map((pronoun) => (
                      <option key={pronoun.arabic} value={pronoun.arabic}>{pronoun.hebrew}</option>
                    ))
                  )}
                </>
              )}
            </select>
              {showPronounTooltip && (
                <div 
                  className="cursor-tooltip"
                  style={{
                    left: tooltipPosition.x + 15,
                    top: tooltipPosition.y + 15
                  }}
                >
                  בחר קודם את הצורה הדקדוקית
                </div>
              )}
            </div>
          </div>
      </div>

      {!showFixPrompt && !showCorrectAnswer && Object.keys(pulseFields).length === 0 && (
        <button 
          className="check-button" 
          onClick={checkAnswers}
          disabled={!allAnswered}
        >
          בדיקה
        </button>
      )}

      {showFixPrompt && (
        <>
          <div className="overlay"></div>
          <div className="fix-prompt">
            <p>תרצו לתקן?</p>
            <div className="fix-buttons">
              <button onClick={handleFix} className="fix-yes">כן, אתקן</button>
              <button onClick={handleContinue} className="fix-no">לא, הצג תשובה נכונה</button>
            </div>
          </div>
        </>
      )}

      {Object.keys(pulseFields).length > 0 && (
        <button 
          className="check-button" 
          onClick={handleNextAfterCorrectAnswer}
        >
          המשך
        </button>
      )}

    </div>
  );
}

export default VerbAnalysisGameComplete;
